import React, { useEffect, useMemo, useState } from 'react';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const ROOM_GROUP_LABELS = {
  global: 'Comunidade',
  campeonato: 'Campeonato',
  jogo: 'Jogo',
  time: 'Times',
  support: 'Operação',
  operational: 'Operação',
};

const ROOM_SCOPE_DESCRIPTIONS = {
  global: 'Arquibancada aberta para torcedores, jogadores e comunidade geral.',
  campeonato: 'Canal oficial da competição com resenha, avisos e conversa da rodada.',
  jogo: 'Sala da partida para viver lances, torcida e acontecimentos em tempo real.',
  time: 'Espaço da torcida do time, elenco, bastidores e pertencimento.',
  support: 'Canal restrito para árbitros, juízes e organizadores.',
  operational: 'Canal restrito para árbitros, juízes e organizadores.',
};

const ROOM_SCOPE_EMOJIS = {
  global: '◇',
  campeonato: '★',
  jogo: '●',
  time: '◉',
  support: '!',
  operational: '!',
};

const QUICK_REACTIONS = ['Gol!', 'Bora pra cima', 'Que defesa!', 'Torcida presente', 'Transmissão no ar'];

export function ChatPage({ loading, error, roomsData, messagesData, selectedRoomId, onSelectRoom, onSendMessage, sending }) {
  const [content, setContent] = useState('');
  const rooms = roomsData?.items || [];
  const messages = messagesData?.items || [];
  const groupedRooms = useMemo(() => groupRooms(rooms), [rooms]);

  const selectedRoom = useMemo(
    () => rooms.find((room) => Number(room.id) === Number(selectedRoomId)) || null,
    [rooms, selectedRoomId],
  );

  useEffect(() => {
    if (!selectedRoomId && rooms.length) {
      onSelectRoom(rooms[0].id);
    }
  }, [rooms, selectedRoomId, onSelectRoom]);

  const latestMessages = messages.slice(-4).reverse();
  const selectedScope = selectedRoom?.scope || 'global';

  if (loading) return <LoadingBlock label="Abrindo Arquibancada Viva..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid social-stand">
      <section className="social-hero">
        <div>
          <span className="eyebrow">Arquibancada Viva</span>
          <h3>{selectedRoom ? selectedRoom.name : 'Comunidade Perifa Pro'}</h3>
          <p>{selectedRoom ? ROOM_SCOPE_DESCRIPTIONS[selectedScope] : 'A camada social que conecta torcida, jogo, times e operação.'}</p>
        </div>
        <div className="social-presence-card">
          <span className="live-dot is-live" />
          <strong>{rooms.length} salas ativas</strong>
          <p>{messages.length} mensagens nesta sala • polling leve, sem custo de tempo real pesado.</p>
        </div>
      </section>

      <div className="chat-grid social-chat-grid">
        <SectionCard title="Canais da torcida" subtitle="Escolha onde a conversa acontece: comunidade, partida, time ou operação.">
        <div className="social-summary-strip">
          <div><strong>{rooms.length}</strong><span>salas</span></div>
          <div><strong>{messages.length}</strong><span>mensagens</span></div>
          <div><strong>{selectedRoom ? ROOM_GROUP_LABELS[selectedRoom.scope] || 'Sala' : '-'}</strong><span>contexto</span></div>
        </div>
        <div className="chat-room-groups">
          {Object.entries(groupedRooms).map(([groupName, groupRooms]) => (
            <div key={groupName} className="chat-room-group">
              <div className="chat-room-group__header">
                <strong>{groupName}</strong>
                <span>{groupRooms.length} sala(s)</span>
              </div>
              <div className="room-list">
                {groupRooms.map((room) => (
                  <button
                    key={room.id}
                    className={`room-button ${Number(room.id) === Number(selectedRoomId) ? 'is-selected' : ''}`}
                    onClick={() => onSelectRoom(room.id)}
                  >
                    <strong><span className="room-sigil">{ROOM_SCOPE_EMOJIS[room.scope] || '•'}</span>{room.name}</strong>
                    <span>{ROOM_SCOPE_DESCRIPTIONS[room.scope] || 'Sala operacional da plataforma.'}</span>
                    <small>{(room.messages_count || 0)} mensagens</small>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        </SectionCard>

      <SectionCard
        title={selectedRoom ? `Resenha: ${selectedRoom.name}` : 'Mensagens'}
        subtitle="Torcida, avisos oficiais e operação conectados ao campeonato."
      >
        {selectedRoom ? (
          <div className="callout-box compact-box social-room-banner">
            <span className="eyebrow">{ROOM_GROUP_LABELS[selectedRoom.scope] || 'Sala'}</span>
            <strong>{selectedRoom.name}</strong>
            <p>{ROOM_SCOPE_DESCRIPTIONS[selectedRoom.scope] || 'Canal de comunicação operacional da plataforma.'}</p>
          </div>
        ) : null}
        <div className="reaction-row">
          {QUICK_REACTIONS.map((reaction) => (
            <button
              key={reaction}
              className="reaction-chip"
              type="button"
              disabled={!selectedRoomId || sending}
              onClick={() => onSendMessage(selectedRoomId, { content: reaction })}
            >
              {reaction}
            </button>
          ))}
        </div>
        <div className="message-feed">
          {messages.length ? messages.map((message) => (
            <article key={message.id} className={`message-card ${message.message_type === 'system' ? 'is-system' : ''}`}>
              <div className="message-card__header">
                <strong>{message.user_name}</strong>
                <span>{message.created_at}</span>
              </div>
              <p>{message.content}</p>
            </article>
          )) : <div className="empty-state">Selecione uma sala para carregar mensagens.</div>}
        </div>

        <form
          className="admin-form message-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!selectedRoomId || !content.trim()) return;
            onSendMessage(selectedRoomId, { content: content.trim() }).then(() => setContent(''));
          }}
        >
          <label>
            Entrar na resenha
            <textarea rows="4" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Comente como torcida, publique aviso oficial ou registre um acontecimento da rodada." />
          </label>
          <button className="primary-button" type="submit" disabled={sending || !selectedRoomId}>
            {sending ? 'Enviando...' : 'Publicar na arquibancada'}
          </button>
        </form>
      </SectionCard>

      <SectionCard title="Pulso da comunidade" subtitle="Leitura rápida do clima da sala selecionada.">
        <div className="stand-pulse-list">
          {latestMessages.length ? latestMessages.map((message) => (
            <article key={message.id} className="stand-pulse-item">
              <span>{message.message_type === 'system' ? '!' : '•'}</span>
              <div>
                <strong>{message.user_name}</strong>
                <p>{message.content}</p>
              </div>
            </article>
          )) : <div className="empty-state">Quando a comunidade falar, os destaques recentes aparecem aqui.</div>}
        </div>
      </SectionCard>
      </div>
    </div>
  );
}

function groupRooms(rooms) {
  return rooms.reduce((accumulator, room) => {
    const groupName = ROOM_GROUP_LABELS[room.scope] || 'Outras salas';
    if (!accumulator[groupName]) {
      accumulator[groupName] = [];
    }
    accumulator[groupName].push(room);
    return accumulator;
  }, {});
}
